import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import type { ZeitreihePunkt } from '../../../lib/apiStatsApi';

interface ZeitreiheChartProps {
  punkte: ZeitreihePunkt[];
  aufloesung: 'stunde' | 'tag' | 'woche';
  darkMode: boolean;
  hoehe?: number;
}

function beschriftung(zeitpunkt: string, aufloesung: 'stunde' | 'tag' | 'woche'): string {
  if (aufloesung === 'stunde') return zeitpunkt.slice(11); // "14:00"
  const [, monat, tag] = zeitpunkt.split('-');
  if (aufloesung === 'woche') return `ab ${tag}.${monat}.`;
  return `${tag}.${monat}.`;
}

/** Verlauf als Balken, Fehler als abgesetzte zweite Reihe — daran erkennt man
 *  Ausfälle und Lastspitzen sofort. recharts liegt bereits im Projekt
 *  (Nutzung-Tab), es wird keine neue Bibliothek eingeführt. */
export default function ZeitreiheChart({ punkte, aufloesung, darkMode, hoehe = 260 }: ZeitreiheChartProps) {
  const leer = punkte.every(p => p.aufrufe === 0 && p.fehler === 0);
  if (leer) {
    return (
      <p className={`text-sm py-10 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        Keine Aufrufe im Zeitraum
      </p>
    );
  }

  const daten = punkte.map(p => ({ ...p, label: beschriftung(p.zeitpunkt, aufloesung) }));
  const achse = darkMode ? '#94a3b8' : '#64748b';
  const gitter = darkMode ? '#334155' : '#e2e8f0';

  return (
    <div style={{ width: '100%', height: hoehe }}>
      <ResponsiveContainer>
        <BarChart data={daten} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gitter} vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 12, fill: achse }} interval="preserveStartEnd" tickLine={false} />
          <YAxis tick={{ fontSize: 12, fill: achse }} allowDecimals={false} tickLine={false} axisLine={false} />
          <Tooltip
            labelFormatter={(_, nutzlast) => (nutzlast?.[0]?.payload as { zeitpunkt?: string })?.zeitpunkt ?? ''}
            contentStyle={darkMode
              ? { backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }
              : { borderRadius: 8 }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="aufrufe" name="Aufrufe" fill="#3b82f6" radius={[3, 3, 0, 0]} />
          <Bar dataKey="fehler" name="Fehler" fill="#ef4444" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
